import { Controller } from "@hotwired/stimulus"
import { baseKit } from "../shared/browserKit"

// usage
/*
  <!-- 기본 사용법: form에 적용 -->
  <form data-controller="makase-json-fetch"
        action="/api/endpoint"
        method="post"
  >
    <input type="hidden" name="id" value="123">
    <button type="submit">저장</button>
  </form>

  <!-- 고급 사용법: body append, petite-vue 이벤트 핸들링 -->
  <form data-controller="makase-json-fetch"
        action="/api/endpoint"
        method="put"
        data-makase-json-fetch-append-value='[["fieldset", "schedule_estimate_time_is"]]'
        @makase-json-fetch:resolved="jsonFetchFormResolve"
        @makase-json-fetch:rejected="jsonFetchFormReject"
  >
    <input type="hidden" name="id" value="123">
    <button type="submit">저장</button>
  </form>

  <!-- stimulus action으로 이벤트 처리 -->
  <form data-controller="makase-json-fetch"
        action="/api/endpoint"
        data-action="
          makase-json-fetch:resolved->otherController#handleResolved
          makase-json-fetch:rejected->otherController#handleRejected
        "
  >
    ...
  </form>

  <!-- before-submit 이벤트로 submit 취소 가능 -->
  <form data-controller="makase-json-fetch"
        @makase-json-fetch:before-submit="handleBeforeSubmit"
  >
    ...
  </form>
  // handleBeforeSubmit(event) {
  //   if (!isValid) {
  //     event.preventDefault() // submit 취소
  //   }
  // }

  <!-- 이벤트를 통한 제어 -->
  element.dispatchEvent(new CustomEvent("makase-json-fetch:submit"))

  <!-- 이벤트 detail 구조 -->
  before-submit: { form: HTMLFormElement } - preventDefault()로 submit 취소 가능
  resolved: { response: Response, data: any }
  rejected: { error: Error, message: string }
*/

export default baseKit(({ formToFetchConfig, jsonFetch, plainFormData }) => {
  return class MakaseJsonFetchController extends Controller {
    // this.appendValue : string (JSON array of [key, value] pairs)
    // this.errorFallbackValue : string (기본 에러 메시지)
    // this.disableOnSubmitValue : boolean (submit 시 버튼 비활성화)
    static values = {
      append: { type: String, default: "" },
      errorFallback: { type: String, default: "요청 처리 중 오류가 발생했습니다." },
      disableOnSubmit: { type: Boolean, default: true },
    }

    connect() {
      this.isSubmitting = false

      // 이벤트 리스너 바인딩
      this.boundSubmit = this.handleSubmitEvent.bind(this)
      this.boundHandleSubmitEvent = this.requestSubmit.bind(this)

      // form submit 이벤트 자동 연결
      if (this.element.tagName === "FORM") {
        this.element.addEventListener("submit", this.boundSubmit)
      }

      // 커스텀 이벤트 리스너
      this.element.addEventListener("makase-json-fetch:submit", this.boundHandleSubmitEvent)
    }

    disconnect() {
      if (this.element.tagName === "FORM") {
        this.element.removeEventListener("submit", this.boundSubmit)
      }
      this.element.removeEventListener("makase-json-fetch:submit", this.boundHandleSubmitEvent)
    }

    requestSubmit(event) {
      event?.stopPropagation?.()
      if (this.element.tagName === "FORM") {
        this.element.requestSubmit()
      }
    }

    async handleSubmitEvent(event) {
      event?.preventDefault()
      event?.stopPropagation?.()

      if (this.isSubmitting) return

      // submitter 정보 저장 (버튼의 name/value가 폼 데이터에 포함됨)
      const submitter = event?.submitter ?? null

      // 1단계 : 폼과 데이터를 준비
      let form = null
      let fetchConfig = null
      try {
        form = this.element.tagName === "FORM" ? this.element : event?.target?.closest("form")

        if (!form) {
          console.warn("makase-json-fetch: form element not found")
          return
        }

        // 공통 formToFetchConfig 사용 (submitter 전달)
        fetchConfig = formToFetchConfig(form, submitter)

        // append 값 추가
        if (this.appendValue) {
          try {
            const appendData = JSON.parse(this.appendValue)
            if (Array.isArray(appendData)) {
              appendData.forEach(([key, value]) => {
                if (fetchConfig.body instanceof FormData) {
                  fetchConfig.body.append(key, value)
                }
              })
            }
          } catch (parseError) {
            console.warn("makase-json-fetch: append parse error", parseError)
          }
        }
      } catch (error) {
        // 실패 이벤트 발생 (레거시 코드와 동일한 detail 구조)
        this.dispatch("rejected", {
          detail: { error, message: error.message },
          bubbles: true,
        })
        return
      }

      // 3단계 : 제출 처리
      let resumedSubmit = false
      const resumeSubmit = async () => {
        if (resumedSubmit) {
          console.warn("makase-json-fetch: submit already started")
        } else {
          resumedSubmit = true
        }

        this.isSubmitting = true
        const submitButtons = this.getSubmitButtons(form)

        // submit 버튼 비활성화
        if (this.disableOnSubmitValue) {
          this.disableButtons(submitButtons)
        }

        try {
          // 공통 jsonFetch 사용
          const response = await jsonFetch(fetchConfig)

          if (!response.ok) {
            let errorMessage = this.errorFallbackValue
            try {
              const errorData = await response.json()
              errorMessage = errorData.message || errorData.error || errorMessage
            } catch {
              // JSON 파싱 실패 시 기본 메시지 사용
            }
            throw new Error(errorMessage)
          }

          // 성공 이벤트 발생 (레거시 코드와 동일한 detail 구조)
          this.dispatch("resolved", {
            detail: { response },
            bubbles: true,
          })

        } catch (error) {
          // 실패 이벤트 발생 (레거시 코드와 동일한 detail 구조)
          this.dispatch("rejected", {
            detail: { error, message: error.message },
            bubbles: true,
          })

        } finally {
          this.isSubmitting = false

          // submit 버튼 다시 활성화
          if (this.disableOnSubmitValue) {
            this.enableButtons(submitButtons)
          }
        }
      }

      // 2단계 : before-submit 훅 이벤트 발생
      // before-submit 이벤트 발생 - preventDefault()로 취소 가능
      const beforeSubmitEvent = this.dispatch("before-submit", {
        detail: {
          submitter,
          resumeSubmit,
          get entriesData() {
            return fetchConfig.body.entries()
          },
          get plainData() {
            return Object.fromEntries(fetchConfig.body.entries())
          }
        },
        bubbles: true,
        cancelable: true,
      })

      // defaultPrevented이면 submit 중단
      if (beforeSubmitEvent.defaultPrevented) {
        return
      } else {
        if (resumedSubmit === true) {
          console.warn("makase-json-fetch: resumeSubmit has already started. Did you miss event.preventDefault() in the before-submit hook?")
        }
        await resumeSubmit()
      }
    }

    // submit 버튼들 찾기
    getSubmitButtons(form) {
      return form.querySelectorAll('button[type="submit"], input[type="submit"], button:not([type])')
    }

    // 버튼 비활성화
    disableButtons(buttons) {
      buttons.forEach(button => {
        button.disabled = true
        button.dataset.makaseJsonFetchDisabled = "true"
      })
    }

    // 버튼 활성화
    enableButtons(buttons) {
      buttons.forEach(button => {
        if (button.dataset.makaseJsonFetchDisabled === "true") {
          button.disabled = false
          delete button.dataset.makaseJsonFetchDisabled
        }
      })
    }
  }
})
